import React from 'react';

import backIcon from '@assets/img/icons/back.svg';
import { FormTitle } from '@components/admin/FormTitle';
import { Layout } from '@components/admin/Layout';
import { Alert } from '@components/Alert';
import { Button } from '@components/Button';
import { LogForm } from '@components/Form';
import { CheckboxInput } from '@components/Forms/CheckboxInput';
import { TextInput } from '@components/Forms/TextInput';
import { SpinnerModal } from '@components/SpinnerModal';
import { VerticalSpace } from '@components/VerticalSpace';
import { useAddStaff } from '@hooks/mutation/dashboard/useAddStaff';
import { useAgencyList } from '@hooks/queries/dashboard/useAgencyList';
import { mapRoleOptionToAgencyId, mapRoleOptionToRole, getRoleOptions } from '@models/Role';
import { StaffFormFields } from '@models/StaffForm';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { FormError } from '@utils/errors';
import { createValidate, formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { PageMeta } from '@utils/pages';
import clsx from 'clsx';
import { Form, Formik } from 'formik';
import { Prompt, useHistory } from 'react-router-dom';

const strings = createStrings({
  ja: {
    title: '新規追加',
    backToList: '一覧へ戻る',
    employeeId: '社員ID',
    name: '名前',
    email: 'メール',
    password: 'パスワード',
    inviteEmployee: '社員に通知を送信',
    role: '権限グループ',
    becomeMember: 'D2D Members権限を付与する',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexColumn,
    flex: 1,
    padding: '1.5rem 2rem',
  },
  backToList: {
    ...common.middleText,
    ...common.fontWeight500,
    color: theme.palette.grey[700],
    marginLeft: 9,
    cursor: 'pointer',
  },
  formBodyContainer: {
    ...common.flexColumn,
    alignItems: 'center',
    paddingTop: '2rem',
    flexGrow: 1,
    rowGap: '20px',
  },
  formBody: {
    ...common.flexColumn,
    alignItems: 'left',
    rowGap: '20px',
  },
  label: {
    ...common.middleText,
    color: theme.palette.grey[800],
  },
  input: {
    ...common.middleText,
  },
  textInput: {
    borderColor: theme.palette.grey[400],
    width: 300,
  },
  selectInput: {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.grey[500],
      width: 300,
    },
  },
  rowContainer: {
    ...common.flexCenter,
    width: '100%',
  },
  button: {
    height: 40,
    borderRadius: 20,
    padding: '5px 36px',
    marginLeft: 10,
  },
  alert: {
    marginTop: '2rem',
  },
}));

const schema = StaffFormFields.shared.toCreateStaffSchema();

const initialValue = StaffFormFields.shared.toCreateStaffInitialValue();

export const AddStaff: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const history = useHistory();

  const { mutateAsync, isLoading, isError, error, reset } = useAddStaff();
  const agencyList = useAgencyList();

  return (
    <Layout title={strings.title}>
      <SpinnerModal open={isLoading || agencyList.isLoading} />
      <div className={classes.container}>
        <FormTitle
          title={strings.title}
          presetActionNode={
            <div
              className={clsx(classes.flexCenter)}
              onClick={() => {
                history.push('Staffs');
              }}
            >
              <img alt={strings.backToList} src={backIcon} width={8} height={14} />
              <span className={clsx(classes.backToList)}>{strings.backToList!}</span>
            </div>
          }
        />
        <Formik
          initialValues={initialValue}
          validate={createValidate(schema)}
          onSubmit={async (values) => {
            await mutateAsync({
              query: { become_member: values.become_member.length > 0 },
              body: {
                ...values,
                role: mapRoleOptionToRole(values.role),
                agency_id: mapRoleOptionToAgencyId(values.role),
              },
            });
            history.push('Staffs');
          }}
        >
          {({ dirty, isSubmitting }) => (
            <Form className={classes.formBodyContainer}>
              <Prompt when={dirty && !isSubmitting} message={() => formStrings.promptLeaving} />

              <div className={clsx(classes.formBody)}>
                <LogForm name="addstaff" />
                <TextInput
                  label={`${strings.employeeId}:`}
                  name="staff_id"
                  type="text"
                  formClasses={{
                    label: classes.label,
                  }}
                  classes={{
                    textInput: clsx(classes.textInput),
                    input: classes.input,
                  }}
                />
                <TextInput
                  label={`${StaffFormFields.shared.lastName.label}:`}
                  name="last_name"
                  type="text"
                  formClasses={{
                    label: classes.label,
                  }}
                  placeholder="山田"
                  classes={{
                    textInput: clsx(classes.textInput),
                    input: classes.input,
                  }}
                />
                <TextInput
                  label={`${StaffFormFields.shared.firstName.label}:`}
                  name="first_name"
                  type="text"
                  formClasses={{
                    label: classes.label,
                  }}
                  placeholder="太郎"
                  classes={{
                    textInput: clsx(classes.textInput),
                    input: classes.input,
                  }}
                />
                <TextInput
                  label={`${strings.role}:`}
                  name="role"
                  formClasses={{
                    label: classes.label,
                  }}
                  classes={{
                    textInput: clsx(classes.textInput),
                    input: classes.input,
                  }}
                  choose
                  options={getRoleOptions(agencyList.data ?? [])}
                />
                <TextInput
                  label={`${strings.email}:`}
                  name="email"
                  type="text"
                  autoComplete="email"
                  formClasses={{
                    label: classes.label,
                  }}
                  classes={{
                    textInput: clsx(classes.textInput),
                    input: classes.input,
                  }}
                />
                <TextInput
                  label={`${strings.password}:`}
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  formClasses={{
                    label: classes.label,
                  }}
                  classes={{
                    textInput: clsx(classes.textInput),
                    input: classes.input,
                  }}
                />

                <div style={{ paddingLeft: '7rem' }}>
                  <CheckboxInput name="become_member" value="Y" label={strings.becomeMember} />
                </div>

                <VerticalSpace />

                <div className={classes.rowContainer}>
                  <Button
                    text={strings.inviteEmployee!}
                    color="primary"
                    shape="round"
                    type="submit"
                  />
                </div>
                <div className={classes.rowContainer}>
                  <Alert
                    open={isError}
                    severity="error"
                    onClose={reset}
                    classes={{ container: classes.alert }}
                  >
                    {error instanceof FormError && error.getDisplayMessage()}
                  </Alert>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Layout>
  );
};

export const meta: PageMeta = {
  title: strings.title,
  order: 2,
};
